import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  type ComponentProps,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useId,
  useState,
} from 'react';
import { de } from '@/i18n/de';
import { t } from '@/i18n/t';
import { cn } from '@/shell/lib/cn';

/**
 * Knopfstile (Plan 9.2): sechs Pixel Radius für Bedienelemente, Zielgröße mindestens 44 px auf dem Handy und
 * 40 px am Desktop, Fokusring kommt global aus dem Stylesheet (2 px Lagune, 2 px Abstand).
 */
export const buttonStyles = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-control border font-semibold whitespace-nowrap transition-colors duration-150 select-none aria-disabled:cursor-not-allowed aria-disabled:opacity-60 disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'border-transparent bg-accent text-on-accent hover:bg-[color-mix(in_oklab,var(--accent),var(--ink)_14%)]',
        secondary: 'border-line-strong bg-surface-block text-ink hover:bg-surface-sunken',
        quiet: 'border-transparent bg-transparent text-accent hover:bg-surface-sunken',
        danger:
          'border-transparent bg-critical text-on-accent hover:bg-[color-mix(in_oklab,var(--critical-ink),var(--ink)_14%)]',
      },
      size: {
        md: 'min-h-11 px-4 text-body sm:min-h-10',
        lg: 'min-h-12 px-5 text-lead',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export type ButtonProps = Omit<ComponentProps<'button'>, 'color'> &
  VariantProps<typeof buttonStyles> & {
    /** Statt eines Knopfes ein anderes Element (z. B. einen Link) mit denselben Stilen darstellen. */
    asChild?: boolean;
    /**
     * Grund, warum der Knopf gerade nicht geht. Der Knopf bleibt fokussierbar (`aria-disabled`), sperrt
     * Klicks und verweist per `aria-describedby` auf den Grund. Der Grund steht sichtbar unter dem Knopf;
     * seitenweite Gründe (Speichern, keine Verbindung, Demo) erst bei Fokus, Zeiger oder Antippen.
     */
    disabledReason?: string;
  };

/**
 * Gründe, die die ganze Seite betreffen und oben schon stehen (Speichern läuft, keine Verbindung, Demo-Modus). Sie
 * erscheinen erst bei Fokus, Zeiger oder Antippen, sonst stünde derselbe Satz unter jedem Knopf der Seite.
 */
const PAGE_WIDE_REASONS: ReadonlySet<string> = new Set([
  de.ui.saving,
  de.states.offlineNoData,
  de.problem.offline,
  de.demo.notPossible,
  de.demo.switching,
]);

/** Breitenklassen des Knopfes, die die Hülle übernehmen muss, damit der Knopf mit Grund so breit bleibt. */
const WIDTH_CLASS = /^(?:[a-z0-9-]+:)*(?:w-|flex-1$|grow$|self-|justify-self-)/;

function wrapperWidth(className: string | undefined): string[] {
  return (className ?? '').split(/\s+/).filter((name) => WIDTH_CLASS.test(name));
}

type RevealHandlers = Pick<
  ButtonProps,
  'onFocus' | 'onBlur' | 'onPointerDown' | 'onPointerEnter' | 'onPointerLeave'
>;

/**
 * Merkt, ob ein seitenweiter Sperrgrund gerade eingeblendet sein soll: bei Fokus (Tastatur), Zeiger (Maus) oder
 * Antippen (Handy, auch ohne Fokus). Gibt die zusammengesetzten Ereignisse für den Knopf zurück.
 */
function useReveal(blocked: boolean, own: RevealHandlers) {
  const [focused, setFocused] = useState(false);
  const [pointed, setPointed] = useState(false);
  const [tapped, setTapped] = useState(false);
  const handlers = {
    onFocus: (event: FocusEvent<HTMLButtonElement>) => {
      setFocused(true);
      own.onFocus?.(event);
    },
    onBlur: (event: FocusEvent<HTMLButtonElement>) => {
      setFocused(false);
      setTapped(false);
      own.onBlur?.(event);
    },
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      setTapped(blocked && event.pointerType !== 'mouse');
      own.onPointerDown?.(event);
    },
    onPointerEnter: (event: PointerEvent<HTMLButtonElement>) => {
      setPointed(event.pointerType === 'mouse');
      own.onPointerEnter?.(event);
    },
    onPointerLeave: (event: PointerEvent<HTMLButtonElement>) => {
      setPointed(false);
      own.onPointerLeave?.(event);
    },
  };
  return { revealed: focused || pointed || tapped, tap: () => setTapped(true), handlers };
}

/** Der Sperrgrund: dauerhaft sichtbar, bei seitenweiten Gründen nur eingeblendet (sonst nur für Vorlesegeräte). */
function DisabledReason({
  id,
  reason,
  revealed,
}: {
  id: string;
  reason: string;
  revealed: boolean;
}) {
  const pageWide = PAGE_WIDE_REASONS.has(reason);
  const shown = !pageWide || revealed;
  const state = pageWide ? (shown ? 'eingeblendet' : 'bei-fokus') : 'sichtbar';
  return (
    <span
      id={id}
      data-sperrgrund={state}
      className={
        shown
          ? cn(
              'max-w-[40ch] text-left text-fine font-normal whitespace-normal text-ink-muted',
              pageWide &&
                'absolute top-full left-0 z-20 mt-1 w-max rounded-control border border-line bg-surface-block px-2 py-1 shadow-sm',
            )
          : 'sr-only'
      }
    >
      {t('ui.disabledBecause', { reason })}
    </span>
  );
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  disabledReason,
  onClick,
  onFocus,
  onBlur,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  type,
  children,
  ...props
}: ButtonProps) {
  const reasonId = useId();
  const Component = asChild ? Slot : 'button';
  const blocked = disabledReason !== undefined;
  const reveal = useReveal(blocked, {
    onFocus,
    onBlur,
    onPointerDown,
    onPointerEnter,
    onPointerLeave,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      event.preventDefault();
      reveal.tap();
      return;
    }
    onClick?.(event);
  };

  // Gleiche Hülle in jedem Zustand: der Knopf wird beim Wechsel frei/gesperrt nie neu eingehängt (Fokus bleibt).
  // Ohne Grund verschwindet die Hülle aus dem Layout (`contents`).
  return (
    <span
      className={
        blocked
          ? cn(
              'relative inline-flex max-w-full flex-col items-start gap-1 align-middle',
              wrapperWidth(className),
            )
          : 'contents'
      }
    >
      <Component
        data-variant={variant ?? 'secondary'}
        className={cn(buttonStyles({ variant, size }), className)}
        {...(asChild ? {} : { type: type ?? 'button' })}
        {...(blocked ? { 'aria-disabled': true, 'aria-describedby': reasonId } : {})}
        onClick={handleClick}
        {...reveal.handlers}
        {...props}
      >
        {children}
      </Component>
      {blocked ? (
        <DisabledReason id={reasonId} reason={disabledReason} revealed={reveal.revealed} />
      ) : null}
    </span>
  );
}

type IconButtonProps = Omit<ButtonProps, 'children' | 'aria-label' | 'asChild'> & {
  /** Pflicht: ein Symbolknopf ohne Namen wäre für Vorlesegeräte leer. */
  'aria-label': string;
  children: ReactNode;
};

/** Knopf nur mit Symbol; Name und Tooltip kommen aus `aria-label`. */
export function IconButton({
  className,
  'aria-label': label,
  children,
  ...props
}: IconButtonProps) {
  return (
    <Button
      aria-label={label}
      title={label}
      className={cn('min-w-11 px-0 sm:min-w-10', className)}
      {...props}
    >
      {children}
    </Button>
  );
}
